import {USDI_API_EUROPE_WEST_URL} from '../configs/config';

/**
 * Poll until a transaction is confirmed on-chain, or throw with a dropped/expired classification.
 */
export async function waitForTransactionConfirmation(connection, signature, options = {}) {
    const {maxAttempts = 60, intervalMs = 1000, lastValidBlockHeight} = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const {value} = await connection.getSignatureStatuses(
            [signature],
            {searchTransactionHistory: true}
        );
        const status = value?.[0];

        if (status) {
            if (status.err) {
                const err = new Error(`Transaction failed on-chain: ${JSON.stringify(status.err)}`);
                err.confirmationResult = 'on_chain_error';
                throw err;
            }
            if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') {
                return {confirmationResult: 'confirmed', status};
            }
        }

        if (lastValidBlockHeight != null) {
            const currentBlockHeight = await connection.getBlockHeight('confirmed');
            if (currentBlockHeight > lastValidBlockHeight) {
                const err = new Error('Transaction expired before confirmation (blockhash stale).');
                err.confirmationResult = 'dropped';
                throw err;
            }
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    const {value} = await connection.getSignatureStatuses(
        [signature],
        {searchTransactionHistory: true}
    );
    const finalStatus = value?.[0];

    if (!finalStatus) {
        const err = new Error('Transaction was not found on-chain (dropped).');
        err.confirmationResult = 'dropped';
        throw err;
    }
    if (finalStatus.err) {
        const err = new Error(`Transaction failed on-chain: ${JSON.stringify(finalStatus.err)}`);
        err.confirmationResult = 'on_chain_error';
        throw err;
    }
    if (finalStatus.confirmationStatus === 'confirmed' || finalStatus.confirmationStatus === 'finalized') {
        return {confirmationResult: 'confirmed', status: finalStatus};
    }

    const err = new Error('Transaction could not be confirmed in time.');
    err.confirmationResult = 'timeout';
    throw err;
}

/**
 * Fire-and-forget telemetry POST for Cloud Run investigation.
 * Backend route: POST /log_transaction_telemetry (usdi_api Cloud Run).
 */
export async function logTransactionTelemetry(payload) {
    const body = {
        ...payload,
        logged_at_utc: new Date().toISOString(),
    };

    console.info('[tx-telemetry]', body);

    try {
        const response = await fetch(`${USDI_API_EUROPE_WEST_URL}/log_transaction_telemetry`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            console.warn('[tx-telemetry] backend returned', response.status);
        }
    } catch (error) {
        console.warn('[tx-telemetry] failed to send:', error.message);
    }
}

export function buildTimingFields({quoteTimestamp, signTimestamp, sendTimestamp}) {
    const quoteMs = quoteTimestamp ?? null;
    const signMs = signTimestamp ?? null;
    const sendMs = sendTimestamp ?? null;

    return {
        quote_timestamp_utc: quoteMs ? new Date(quoteMs).toISOString() : null,
        sign_timestamp_utc: signMs ? new Date(signMs).toISOString() : null,
        send_timestamp_utc: sendMs ? new Date(sendMs).toISOString() : null,
        quote_to_sign_ms: quoteMs != null && signMs != null ? signMs - quoteMs : null,
        sign_to_send_ms: signMs != null && sendMs != null ? sendMs - signMs : null,
        quote_to_send_ms: quoteMs != null && sendMs != null ? sendMs - quoteMs : null,
    };
}
